/**
 * @file UserCreateModal.tsx
 * @description Модалка «Создать пользователя» (phase08.5 shadcn).
 *
 * Единый flow: внутри одной модалки админ выбирает привязку
 * («Платформа» или конкретный магазин) — и сразу видит список ролей,
 * соответствующих категории. Бэкенд тот же: сначала POST /admin/users,
 * затем POST /admin/users/:id/contexts.
 *
 * Поведение по scope:
 *   - scope=platform: селектор «Куда привязать?» доступен, опции
 *     «Платформа» + список магазинов; роль обязательна только для SHOP.
 *   - scope=shop: привязка зафиксирована на этот магазин (без селектора),
 *     роль обязательна — это поведение унаследовано из phase04 шага 6.
 */

import { useEffect, useState } from "react";

import { Alert } from "./ui/alert";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  addAdminUserContext,
  createAdminUser,
  type AdminUser,
} from "../lib/api-admin-users";
import { listAdminRoles, type AdminRole } from "../lib/api-admin-roles";
import { ApiError } from "../lib/api-client";

interface ShopRef {
  id: string;
  slug: string;
  displayName: string;
}

interface ShopScope {
  scope: "shop";
  shopId: string;
  shopDisplayName: string;
}
interface PlatformScope {
  scope: "platform";
  /** Магазины, в которые админ может назначить пользователя сразу. */
  shops?: ShopRef[];
}

export type UserCreateScope = ShopScope | PlatformScope;

interface UserCreateModalProps {
  open: boolean;
  scope: UserCreateScope;
  onClose: () => void;
  onCreated: (user: AdminUser) => void;
}

interface FormState {
  email: string;
  password: string;
  displayName: string;
  roleId: string;
}

const EMPTY_FORM: FormState = { email: "", password: "", displayName: "", roleId: "" };

/** Внутренняя привязка, к которой будет добавлен контекст. */
type Binding =
  | { kind: "platform" }
  | { kind: "shop"; shopId: string; shopDisplayName: string };

function initialBinding(scope: UserCreateScope): Binding {
  return scope.scope === "shop"
    ? { kind: "shop", shopId: scope.shopId, shopDisplayName: scope.shopDisplayName }
    : { kind: "platform" };
}

function bindingKey(b: Binding): string {
  return b.kind === "platform" ? "__platform__" : `shop:${b.shopId}`;
}

export function UserCreateModal({
  open,
  scope,
  onClose,
  onCreated,
}: UserCreateModalProps) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [binding, setBinding] = useState<Binding>(() => initialBinding(scope));
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bindingLocked = scope.scope === "shop";
  const availableShops: ShopRef[] = scope.scope === "platform" ? scope.shops ?? [] : [];
  const showBindingPicker = !bindingLocked && availableShops.length > 0;

  // При открытии — сброс формы и привязки.
  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setBinding(initialBinding(scope));
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Подгрузка ролей по текущей привязке. Сбрасываем уже выбранную роль —
  // при смене PLATFORM ↔ SHOP старый roleId становится невалидным.
  useEffect(() => {
    if (!open) return;
    setLoadingRoles(true);
    setForm((f) => ({ ...f, roleId: "" }));
    const cat = binding.kind === "shop" ? "SHOP" : "PLATFORM";
    listAdminRoles(cat)
      .then(setRoles)
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Не удалось загрузить роли");
      })
      .finally(() => setLoadingRoles(false));
  }, [open, binding.kind]);

  const roleRequired = binding.kind === "shop";

  function onChangeBinding(value: string) {
    if (value === "__platform__") {
      setBinding({ kind: "platform" });
      return;
    }
    const shop = availableShops.find((s) => s.id === value);
    if (shop) {
      setBinding({ kind: "shop", shopId: shop.id, shopDisplayName: shop.displayName });
    }
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (form.password.length < 10) {
      setError("Пароль должен быть не короче 10 символов");
      return;
    }
    if (roleRequired && !form.roleId) {
      setError("Выберите роль для магазина");
      return;
    }
    setSubmitting(true);
    try {
      const user = await createAdminUser({
        email: form.email.trim(),
        password: form.password,
        displayName: form.displayName.trim() || undefined,
      });
      let final: AdminUser = user;
      if (form.roleId) {
        final = await addAdminUserContext(user.id, {
          contextType: binding.kind === "shop" ? "SHOP" : "PLATFORM",
          shopId: binding.kind === "shop" ? binding.shopId : undefined,
          roleId: form.roleId,
        });
      }
      onCreated(final);
      onClose();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Не удалось создать пользователя",
      );
    } finally {
      setSubmitting(false);
    }
  }

  const roleLabel = roleRequired ? "Роль (обязательно)" : "Роль (необязательно)";
  const bindingHint =
    binding.kind === "shop"
      ? `Контекст создаётся в магазине «${binding.shopDisplayName}».`
      : "Платформенный контекст — пользователь получит права на уровне платформы.";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent fullScreen className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            Создать пользователя
            {scope.scope === "shop" ? ` в магазин «${scope.shopDisplayName}»` : ""}
          </DialogTitle>
        </DialogHeader>

        <form
          id="user-create-form"
          onSubmit={(e) => void onSubmit(e)}
          className="flex flex-col gap-3"
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="uc-email">Email</Label>
            <Input
              id="uc-email"
              type="email"
              required
              autoComplete="off"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              disabled={submitting}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="uc-password">Пароль (≥10 символов)</Label>
            <Input
              id="uc-password"
              type="password"
              required
              minLength={10}
              autoComplete="new-password"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              disabled={submitting}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="uc-name">Имя (необязательно)</Label>
            <Input
              id="uc-name"
              type="text"
              value={form.displayName}
              onChange={(e) =>
                setForm((f) => ({ ...f, displayName: e.target.value }))
              }
              disabled={submitting}
            />
          </div>

          {showBindingPicker ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="uc-binding">Куда привязать?</Label>
              <select
                id="uc-binding"
                value={bindingKey(binding)}
                onChange={(e) => onChangeBinding(e.target.value)}
                disabled={submitting}
                className="flex h-10 w-full rounded-md bg-input px-3 py-2 text-sm text-on-surface outline-none focus-visible:bg-surface-lowest focus-visible:[box-shadow:inset_0_-2px_0_var(--primary)] disabled:opacity-50"
              >
                <option value="__platform__">Платформа</option>
                {availableShops.map((s) => (
                  <option key={s.id} value={s.id}>
                    Магазин · {s.displayName}
                  </option>
                ))}
              </select>
              <span className="text-xs text-on-surface-variant">{bindingHint}</span>
            </div>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="uc-role">{roleLabel}</Label>
            <select
              id="uc-role"
              value={form.roleId}
              onChange={(e) => setForm((f) => ({ ...f, roleId: e.target.value }))}
              disabled={submitting || loadingRoles}
              className="flex h-10 w-full rounded-md bg-input px-3 py-2 text-sm text-on-surface outline-none focus-visible:bg-surface-lowest focus-visible:[box-shadow:inset_0_-2px_0_var(--primary)] disabled:opacity-50"
            >
              <option value="">— не назначать —</option>
              {roles.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.displayName} ({r.code})
                </option>
              ))}
            </select>
          </div>

          {error ? <Alert severity="error">{error}</Alert> : null}
        </form>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Отмена
          </Button>
          <Button type="submit" form="user-create-form" disabled={submitting}>
            {submitting ? "Создаём…" : "Создать"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
